import React, { useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import {
  Box,
  Heading,
  HStack,
  FormControl,
  FormLabel,
  Select,
  Text,
} from "@chakra-ui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChartBar } from "@fortawesome/free-solid-svg-icons";
import { StandardPageTitle, StandardHeader } from "../../themes/StandardHeader";

const VaccinePreventionDemandPlanChart = ({ data, unitOptions }) => {
  const [selectedUnit, setSelectedUnit] = useState("");

  // จัดการการเปลี่ยนแปลงใน dropdown
  const handleUnitChange = (e) => {
    setSelectedUnit(e.target.value);
  };

  // กรองหน่วยงานที่มีข้อมูลใน data
  const availableUnits = [
    ...new Set(data.map((item) => item.EstimateUnit)),
  ].filter(Boolean); // กรองค่าที่ไม่ใช่ null/undefined
  const filteredUnitOptions = unitOptions.filter(
    (unit) =>
      unit.UnitName &&
      availableUnits.includes(unit.UnitName)
  );

  // เตรียมข้อมูลสำหรับกราฟ
  const chartData = () => {
    if (!selectedUnit) {
      // รวมยอดทุกหน่วยงานตาม VaccineNameTH
      const groupedData = data.reduce((acc, item) => {
        const vaccineName = item.VaccineNameTH;
        if (!acc[vaccineName]) {
          acc[vaccineName] = {
            name: vaccineName,
            quantity: 0,
            vaccineEN: item.VaccineNameEN,
            unit: "ทุกหน่วยงาน",
          };
        }
        acc[vaccineName].quantity += item.TotalQuantityDemanded;
        return acc;
      }, {});

      return Object.values(groupedData);
    } else {
      // กรองข้อมูลตามหน่วยงานที่เลือก
      return data
        .filter((item) => item.EstimateUnit === selectedUnit)
        .map((item) => ({
          name: item.VaccineNameTH,
          quantity: item.TotalQuantityDemanded,
          vaccineEN: item.VaccineNameEN,
          unit: item.EstimateUnit,
        }));
    }
  };

  // กำหนดสีสำหรับกราฟ
  const barColor = "#16A085"; // Green, matching iGreen.500

  // Tooltip ที่กำหนดเอง
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <Box
          bg="white"
          p={3}
          border="1px solid #ccc"
          borderRadius="md"
          boxShadow="md"
        >
          <p>
            <strong>ชื่อวัคซีน (TH):</strong> {label}
          </p>
          <p>
            <strong>ชื่อวัคซีน (EN):</strong> {data.vaccineEN}
          </p>
          <p>
            <strong>ชื่อหน่วยงาน:</strong> {data.unit}
          </p>
          <p>
            <strong>จำนวนความต้องการ:</strong> {data.quantity}
          </p>
        </Box>
      );
    }
    return null;
  };

  // Debugging: Log data and unitOptions to verify
  console.log("Chart Data:", data);
  console.log("Unit Options:", unitOptions);
  console.log("Available Units:", availableUnits);
  console.log("Filtered Unit Options:", filteredUnitOptions);

  return (
    <Box
      borderWidth={1}
      borderRadius="lg"
      boxShadow="lg"
      p={5}
      mt={6}
      bg="white"
    >
      <Box
        mb={5}
        borderRadius="full"
        w="100%"
        bgColor="iGreen.500"
        color="white"
        p={2}
      >
        <HStack spacing={5} ml={5}>
          <FontAwesomeIcon icon={faChartBar} color="white" size="xl" />
          <Heading size={{ base: "md", md: "lg" }} fontWeight="normal">
            กราฟยอดความต้องการวัคซีน
          </Heading>
        </HStack>
      </Box>
      <FormControl m={5} maxW="300px">
        <FormLabel fontSize="xl" color="gray">
          เลือกหน่วยงาน
        </FormLabel>
        {filteredUnitOptions.length > 0 ? (
          <Select
            value={selectedUnit}
            onChange={handleUnitChange}
            placeholder="ทุกหน่วยงาน"
            colorScheme="iBlue"
          >
            {filteredUnitOptions.map((unit) => (
              <option
                key={unit.UnitID}
                value={unit.UnitName}
              >
                {unit.UnitName}
              </option>
            ))}
          </Select>
        ) : (
          <Text color="gray.500">ไม่มีหน่วยงานที่มีข้อมูล</Text>
        )}
      </FormControl>
      {chartData().length > 0 ? (
        <ResponsiveContainer width="100%" height={500}>
          <BarChart
            data={chartData()}
            margin={{ top: 40, right: 30, left: 20, bottom: 60 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="name"
              angle={-45}
              textAnchor="end"
              interval={0}
              height={150}
              tick={{ fontSize: 12 }}
            />
            <YAxis
              label={{
                value: "จำนวนความต้องการ (หน่วย)",
                angle: -90,
                position: "insideLeft",
                offset: -5,
              }}
              tick={{ fontSize: 12 }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend
              layout="horizontal"
              verticalAlign="top"
              align="right"
              wrapperStyle={{ paddingBottom: 10 }}
            />
            <Bar
              dataKey="quantity"
              name="จำนวนความต้องการ"
              fill={barColor}
              barSize={50}
            />
          </BarChart>
        </ResponsiveContainer>
      ) : (
        <Text textAlign="center" color="gray.500" p={5}>
          ไม่มีข้อมูลสำหรับแสดงกราฟ
        </Text>
      )}
    </Box>
  );
};

export default VaccinePreventionDemandPlanChart;